import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { DownloadFileNameLink } from 'components'
import { Chip } from 'components/chips'
import * as controllers from 'controllers'
import { QuickBooksSyncDto } from 'dtos'
import { useEffect, useState } from 'react'
import { formatDateTime } from 'utils'

interface QuickBooksSyncPreviousVersionsDialogProps {
  onClose: () => void
  open: boolean
}

export default function QuickBooksSyncPreviousVersionsDialog({
  onClose,
  open
}: QuickBooksSyncPreviousVersionsDialogProps) {
  const [isGettingQuickBooksSyncVersions, setIsGettingQuickBooksSyncVersions] = useState<boolean>(false)
  const [quickBooksSyncVersions, setQuickBooksSyncVersions] = useState<QuickBooksSyncDto[]>([])

  useEffect(() => {
    if (!open) return

    setIsGettingQuickBooksSyncVersions(true)
    controllers
      .getQuickBooksSyncVersions()
      .then(response => {
        setQuickBooksSyncVersions(response.value)
      })
      .finally(() => {
        setIsGettingQuickBooksSyncVersions(false)
      })
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Previous QuickBooks Sync Versions</DialogTitle>

      <DialogContent>
        {isGettingQuickBooksSyncVersions && (
          <Grid container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
            <Grid item>
              <CircularProgress color='primary' size={50} />
            </Grid>
          </Grid>
        )}

        {!isGettingQuickBooksSyncVersions && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Uploaded At</TableCell>
                  <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Uploaded By</TableCell>
                  <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Notes</TableCell>
                  <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Application</TableCell>
                  <TableCell sx={theme => ({ color: theme.palette.primary.main })}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {quickBooksSyncVersions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color='text.secondary'>No QuickBooks Sync versions have been uploaded.</Typography>
                    </TableCell>
                  </TableRow>
                )}

                {quickBooksSyncVersions.map(quickBooksSync => (
                  <TableRow key={quickBooksSync.quickBooksSyncGuid}>
                    <TableCell>{quickBooksSync.createdDateTime ? formatDateTime(quickBooksSync.createdDateTime) : '-'}</TableCell>
                    <TableCell>
                      {`${quickBooksSync.createdBy?.firstName ?? ''} ${quickBooksSync.createdBy?.lastName ?? ''}`.trim() ||
                        '-'}
                    </TableCell>
                    <TableCell>{quickBooksSync.notes || '-'}</TableCell>
                    <TableCell>
                      {quickBooksSync.document?.displayFileName ? (
                        <DownloadFileNameLink
                          fileName={quickBooksSync.document.displayFileName}
                          label='Download'
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {quickBooksSync.isActive ? <Chip variant='green'>Active</Chip> : <Chip variant='gray'>Inactive</Chip>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button color='inherit' onClick={onClose} variant='text'>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  )
}
